import type { ConsoleEvent, ConsoleLevel, ErrorEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { serializeArg, parseSource } from './serialize.js';
import { uuid } from './util.js';

const LEVELS: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
const MAX_PER_SEC = 100; // flood cap (§6.1)

export function instrumentConsole(ctx: InstrumentCtx): Teardown {
  const original: Partial<Record<ConsoleLevel, (...a: unknown[]) => void>> = {};
  let windowStart = ctx.clock.now();
  let inWindow = 0;
  let suppressed = 0;

  function rateLimited(): boolean {
    const now = ctx.clock.now();
    if (now - windowStart >= 1000) {
      if (suppressed > 0) {
        // surface a single synthetic note that we dropped a flood
        emitConsole('warn', [
          { kind: 'string', value: `LogClip: suppressed ${suppressed} console messages (flood cap)` },
        ]);
        suppressed = 0;
      }
      windowStart = now;
      inWindow = 0;
    }
    if (inWindow >= MAX_PER_SEC) {
      suppressed++;
      return true;
    }
    inWindow++;
    return false;
  }

  function emitConsole(level: ConsoleLevel, args: ConsoleEvent['args'], stack?: string) {
    const source = parseSource(stack);
    const e: ConsoleEvent = {
      id: uuid(),
      sessionId: ctx.sessionId,
      type: 'console',
      t: ctx.clock.now(),
      ts: ctx.clock.wall(),
      level,
      args,
      stack: level === 'error' ? stack : undefined,
      source,
    };
    ctx.emit(e);
  }

  for (const level of LEVELS) {
    const orig = console[level] as (...a: unknown[]) => void;
    original[level] = orig;
    console[level] = (...args: unknown[]) => {
      try {
        orig.apply(console, args);
      } catch {
        /* never let our wrapper break the original */
      }
      try {
        if (rateLimited()) return;
        const serialized = args.map((a) => serializeArg(a, ctx.config.maxArgBytes));
        let stack: string | undefined;
        if (level === 'error') {
          const errArg = args.find((a) => a instanceof Error) as Error | undefined;
          stack = errArg?.stack ?? new Error().stack;
        }
        emitConsole(level, serialized, stack);
      } catch {
        /* swallow */
      }
    };
  }

  return () => {
    for (const level of LEVELS) {
      if (original[level]) console[level] = original[level]!;
    }
  };
}

/** Shared helper used by errors.ts to also surface uncaught errors in console. */
export function errorToConsoleEvent(ctx: InstrumentCtx, err: ErrorEvent): ConsoleEvent {
  return {
    id: uuid(),
    sessionId: ctx.sessionId,
    type: 'console',
    t: err.t,
    ts: err.ts,
    level: 'error',
    args: [{ kind: 'error', name: err.name, message: err.message, stack: err.stack }],
    stack: err.stack,
    source: err.source,
  };
}
